import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Award, Target, TrendingUp, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';

function About() {
    const scoringCriteria = [
        {
            title: 'Skills Match',
            description: 'Evaluates how well the candidate\'s technical and soft skills align with job requirements',
            weight: '35%',
            icon: Target
        },
        {
            title: 'Experience Relevance',
            description: 'Assesses years of experience and relevance of past roles to the position',
            weight: '30%',
            icon: TrendingUp
        },
        {
            title: 'Education & Certifications',
            description: 'Considers educational background and professional certifications',
            weight: '20%',
            icon: Award
        },
        {
            title: 'Keywords & Context',
            description: 'Analyzes industry-specific terminology and contextual understanding',
            weight: '15%',
            icon: CheckCircle2
        }
    ];

    const scoreRanges = [
        {
            range: '75 - 100',
            label: 'Best Match',
            description: 'Exceptional candidate with strong alignment to all job requirements. Highly recommended for interview.',
            color: 'green',
            icon: CheckCircle2,
            bgColor: 'bg-green-900/20',
            borderColor: 'border-green-500/50',
            textColor: 'text-green-400',
            iconBg: 'bg-green-500/20'
        },
        {
            range: '50 - 74',
            label: 'Good Match',
            description: 'Qualified candidate with moderate alignment. Consider for interview with further screening.',
            color: 'yellow',
            icon: AlertTriangle,
            bgColor: 'bg-yellow-900/20',
            borderColor: 'border-yellow-500/50',
            textColor: 'text-yellow-400',
            iconBg: 'bg-yellow-500/20'
        },
        {
            range: '0 - 49',
            label: 'Poor Match',
            description: 'Limited alignment with job requirements. May not be suitable for current position.',
            color: 'red',
            icon: XCircle,
            bgColor: 'bg-red-900/20',
            borderColor: 'border-red-500/50',
            textColor: 'text-red-400',
            iconBg: 'bg-red-500/20'
        }
    ];

    return (
        <div className="min-h-screen bg-slate-950 text-gray-100">

            {/* Hero Section */}
            <div className="bg-gradient-to-b from-slate-900 to-slate-950 border-b border-slate-800">
                <div className="max-w-6xl mx-auto px-4 py-16">
                    <Link
                        to="/"
                        className="inline-flex items-center text-slate-400 hover:text-white transition-colors mb-8 group"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                        Back to Analyzer
                    </Link>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <h1 className="text-5xl md:text-6xl font-extrabold mb-4 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 text-transparent bg-clip-text">
                            About Resume AI Analyzer
                        </h1>
                        <p className="text-xl text-slate-400 max-w-3xl">
                            Our AI-powered system uses advanced natural language processing and machine learning to provide
                            precise, unbiased resume analysis with strict scoring policies.
                        </p>
                    </motion.div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 py-16 space-y-16">

                {/* Scoring Criteria Section */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <div className="mb-12">
                        <h2 className="text-3xl font-bold mb-4 text-white">Scoring Criteria</h2>
                        <p className="text-slate-400 text-lg">
                            Our AI evaluates resumes based on multiple weighted factors to ensure comprehensive assessment
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {scoringCriteria.map((criteria, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.3 + index * 0.1 }}
                                className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 hover:border-purple-500/50 transition-all group"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="p-3 bg-purple-500/10 rounded-xl group-hover:bg-purple-500/20 transition-colors">
                                        <criteria.icon className="w-6 h-6 text-purple-400" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="font-semibold text-white text-lg">{criteria.title}</h3>
                                            <span className="text-sm font-bold text-purple-400 bg-purple-500/10 px-3 py-1 rounded-full">
                                                {criteria.weight}
                                            </span>
                                        </div>
                                        <p className="text-slate-400 text-sm leading-relaxed">
                                            {criteria.description}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.section>

                {/* Score Interpretation Section */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    <div className="mb-12">
                        <h2 className="text-3xl font-bold mb-4 text-white">Score Interpretation</h2>
                        <p className="text-slate-400 text-lg">
                            Understanding what each score range means for candidate evaluation
                        </p>
                    </div>

                    <div className="space-y-6">
                        {scoreRanges.map((range, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.7 + index * 0.1 }}
                                className={`${range.bgColor} border ${range.borderColor} rounded-2xl p-6 hover:scale-[1.02] transition-all`}
                            >
                                <div className="flex items-start gap-6">
                                    <div className={`p-4 ${range.iconBg} rounded-xl`}>
                                        <range.icon className={`w-8 h-8 ${range.textColor}`} />
                                    </div>

                                    <div className="flex-1">
                                        <div className="flex items-center gap-4 mb-3">
                                            <span className={`text-3xl font-bold ${range.textColor}`}>
                                                {range.range}
                                            </span>
                                            <span className={`text-lg font-semibold ${range.textColor}`}>
                                                {range.label}
                                            </span>
                                        </div>
                                        <p className="text-slate-300 leading-relaxed">
                                            {range.description}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.section>

                {/* Strict Policies Section */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.0 }}
                    className="bg-gradient-to-br from-purple-900/20 to-indigo-900/20 border border-purple-500/30 rounded-3xl p-8 md:p-12"
                >
                    <h2 className="text-3xl font-bold mb-6 text-white flex items-center">
                        <Award className="w-8 h-8 text-purple-400 mr-3" />
                        Strict Scoring Policies
                    </h2>

                    <div className="space-y-4 text-slate-300 leading-relaxed">
                        <p className="text-lg">
                            Our Resume AI Analyzer implements <span className="text-purple-400 font-semibold">rigorous evaluation standards</span> to
                            ensure only the most qualified candidates receive high scores:
                        </p>

                        <ul className="space-y-3 ml-6">
                            <li className="flex items-start">
                                <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
                                <span><strong className="text-white">High Precision Matching:</strong> Candidates must demonstrate clear,
                                    documented evidence of required skills and experience</span>
                            </li>
                            <li className="flex items-start">
                                <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
                                <span><strong className="text-white">Context-Aware Analysis:</strong> Simple keyword matching is insufficient;
                                    the AI evaluates skills in proper context and application</span>
                            </li>
                            <li className="flex items-start">
                                <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
                                <span><strong className="text-white">75+ Score Threshold:</strong> Only candidates scoring 75 or above are
                                    considered "Best Match" - this represents the top tier of applicants</span>
                            </li>
                            <li className="flex items-start">
                                <CheckCircle2 className="w-5 h-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
                                <span><strong className="text-white">Bias-Free Evaluation:</strong> Our AI focuses purely on qualifications,
                                    eliminating unconscious human biases from initial screening</span>
                            </li>
                        </ul>

                        <div className="mt-8 p-6 bg-slate-950/50 rounded-xl border border-slate-700">
                            <p className="text-sm text-slate-400 italic">
                                💡 <strong className="text-slate-300">Pro Tip:</strong> Due to our strict scoring methodology,
                                a score of 75+ indicates an exceptional candidate match. We recommend prioritizing these candidates
                                for interviews while considering 50-74 range candidates based on other factors like cultural fit
                                and growth potential.
                            </p>
                        </div>
                    </div>
                </motion.section>

            </div>
        </div>
    );
}

export default About;
